import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAppState } from '../store/state';
import MaintenancePage from '../components/pages/Maintenance.vue';
import WelcomePage from '../components/pages/OnboardWelcome.vue';
import CreateAccountPage from '../components/pages/OnboardSignup.vue';
import CreateMenuPage from '../components/pages/OnboardMenu.vue';
import ConnectCompanyPage from '../components/pages/OnboardCompany.vue';
import PositionMapPage from '../components/pages/OnboardMap.vue';
import ActivatingPage from '../components/pages/OnboardActivating.vue';
import TermsPage from '../components/pages/OnboardTerms.vue';
import NotFound from '../components/pages/NotFound.vue';
import Login from '../components/pages/Login.vue';
import Onboarding from '../components/Onboarding.vue';
import View from '../components/pages/View.vue';
import NoAccessPage from '../components/pages/OnboardNoAccess.vue';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/maintenance/:uuid?',
    name: 'maintenance',
    component: MaintenancePage,
    meta: { title: 'Wision maintenance' }
  },
  {
    path: '/:uuid',
    name: 'view',
    component: View,
    meta: { title: 'Wision NFC' }
  },
  {
    path: '/not-found',
    name: 'not-found',
    component: NotFound,
  },
  {
    path: '/login',
    name: 'userlogin',
    props: { onboarding: false },
    component: Login,
  },
  {
    path: '/onboard/:uuid',
    component: Onboarding,
    meta: { title: 'Wision Onboarding' },
    children: [
      {
        path: 'login',
        name: 'login',
        component: Login,
      },
      {
        path: 'start',
        name: 'start',
        component: WelcomePage,
      },
      {
        path: 'no-access',
        name: 'no-access',
        component: NoAccessPage,
      },
      {
        path: 'signup',
        name: 'signup',
        component: CreateAccountPage,
      },
      {
        path: 'connect',
        name: 'connect',
        component: ConnectCompanyPage,
      },
      {
        path: 'menu',
        name: 'menu',
        component: CreateMenuPage,
      },
      {
        path: 'terms',
        name: 'terms',
        component: TermsPage
      },
      {
        path: 'map',
        name: 'map',
        component: PositionMapPage,
      },
      {
        path: 'activate',
        name: 'activating',
        component: ActivatingPage,
      },
      {
        path: '',
        redirect: { name: 'start' }
      }
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    component: NotFound
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const state = useAppState();

  const uuid = to.params.uuid;
  const token = to.params.token;

  const maintenance = import.meta.env.VITE_NFC_MAINTENANCE_MODE;

  if (to.name != 'maintenance' && maintenance == 'true') {
    next({ name: 'maintenance', params: { uuid } });
    return;
  }

  if (to.name == 'maintenance' && maintenance !== 'true') {
    if (uuid) {
      next({ name: 'view', params: { uuid } });
    } else {
      next({ name: 'not-found' });
    }

    return;
  }

  const isOnboardingRoute = to.matched.some(r => r.path.startsWith('/onboard'));

  if (to.name === 'not-found' || to.name === 'userlogin' || to.name === 'maintenance') {
    next();
    return;
  }

  // Check session for onboarding routes so components like OnboardMenu
  // can rely on state.hasAccess after OAuth callback redirects
  if (isOnboardingRoute) {
    if (!state.hasCheckedSession)
      await state.authorizeSession();
    next();
    return;
  }

  if (!uuid && !token) {
    next({ name: 'not-found' });
    return;
  }

  if (!uuid) {
    next();
    return;
  }

  if (state.configTag?.config?.organization_id && to.name === 'signup') {
    next({ name: 'no-access' });
    return;
  }

  if (!state.hasCheckedSession)
    await state.authorizeSession();
  if (!state.tag && to.name === 'view') {
    const result = await state.fetchTag(uuid as string);

    if (result?.statusCode === 401) {
      next({ name: 'login', params: { uuid } });
      return;
    }

    if (result?.statusCode === 403) {
      next({ name: 'no-access', params: { uuid } });
      return;
    }

    if (!result?.tag) {
      next({ name: 'not-found' });
      return;
    }
  } else if (!state.tag && to.name !== 'info')
    await state.fetchConfigTag(uuid as string);

  if (state.tag && to.name === 'view') {
    if (state.tag.status === 'pending') {
      next({ name: 'activating', params: { uuid } });
      return;
    }

    if (state.tag.status === 'idle') {
      state.tag = undefined;
      next({ name: 'start', params: { uuid } });
      return;
    }

    next();
    return;
  }

  if (state.configTag && state.configTag.status === 'ready') {
    next({ name: 'view', params: { uuid } });
    return;
  }
  if (!state.configTag) {
    next({ name: 'not-found' });
  }
  else next();
});

export default router;

